import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MdOutlineDoubleArrow } from "react-icons/md";
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo/newlogo/logo6.png';

// LanguageSelector component
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  
  const changeLanguage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <select 
      onChange={changeLanguage} 
      value={i18n.language}
      className="bg-transparent text-white border border-amber-400 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-amber-300"
    >
      <option value="en">English</option>
      <option value="hi">हिंदी</option>
      <option value="ta">தமிழ்</option>
    </select>
  );
};

// Hamburger Component
const Hamburger = ({ onToggle, checked }: { onToggle: (isOpen: boolean) => void, checked: boolean }) => {
  const handleCheckboxChange = () => {
    onToggle(!checked);
  };

  return (
    <label className="hamburger">
      <input
        type="checkbox"
        checked={checked}
        onChange={handleCheckboxChange}
      />
      <svg viewBox="0 0 32 32">
        <path
          className="line line-top-bottom"
          d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
        ></path>
        <path className="line" d="M7 16 27 16"></path>
      </svg>
    </label>
  );
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMenuOpen(false);
  };

  const menuItems = [
    { id: 'home', label: t('menu.home') },
    { id: 'about', label: t('menu.about') },
    { id: 'usecase', label: t('menu.usecase') },
    { id: 'templates', label: t('menu.templates') },
    { id: 'pricing', label: t('menu.pricing') },
    { id: 'benefits', label: t('menu.benefits') },
    { id: 'contact', label: t('menu.contact') },
  ];

  return (
    <>
            <nav className="header font-secondary relative bg-black my-5 flex items-center justify-between px-4 sm:px-8">
        {/* Logo */}
        <motion.div
          className={`transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}
          initial={{ x: -400 }}
          animate={{ x: 0 }}
          transition={{ type: 'spring', duration: 4 }}
        >
          <img src={logo} alt="logo" className="logo md:max-w-[430px] sm:max-w-[230px]" />
        </motion.div>

        {/* Right side controls */}
        <div className="flex items-center gap-4">
          <div className={`hidden sm:flex items-center gap-4 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}>
            <LanguageSelector />
            <a 
              href="tel:+91-7200945514"
              className="boton-elegante"
            >
              {t('header.callNow')}
            </a>
          </div>
          
          {/* Added margin to create space between language selector and hamburger */}
          <div className="sm:hidden mr-3">
            <LanguageSelector />
          </div>
          
          <Hamburger onToggle={setIsMenuOpen} checked={isMenuOpen} />
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 text-white z-50 flex flex-col items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Language selector for mobile - moved further down */}
          {/* <div className="absolute top-24 right-6 sm:top-6">
            <LanguageSelector />
          </div> */}

          {/* Desktop layout - side by side */}
          <div className="hidden md:flex w-full h-full">
            {/* Menu items on left */}
            <div className="flex-1 flex items-center justify-center">
              <ul className="font-spl3 text-5xl leading-relaxed font-black">
                {menuItems.map(({ id, label }) => (
                  <motion.li
                    key={id}
                    className="my-4 flex items-center gap-4 hover:text-amber-300 transition-colors duration-300"
                    whileHover={{ x: -10 }}
                  >
                    <MdOutlineDoubleArrow className="text-amber-400" />
                    <button onClick={() => scrollToSection(id)} className="focus:outline-none"  aria-label="Scroll to Features Section">
                      {label}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>
            
            {/* Logo on right */}
            <div className="flex-1 flex items-center justify-center">
              <img src={logo} className="w-[600px] max-w-full" alt="logo" />
            </div>
          </div>

          {/* Mobile layout - no logo */}
          <div className="md:hidden w-full">
            <ul className="text-center font-spl3 text-4xl leading-relaxed font-black">
              {menuItems.map(({ id, label }) => (
                <motion.li
                  key={id}
                  className="my-4 flex items-center justify-center gap-4 hover:text-amber-300 transition-colors duration-300"
                  whileHover={{ x: -10 }}
                >
                  <MdOutlineDoubleArrow className="text-amber-400" />
                  <button onClick={() => scrollToSection(id)} className="focus:outline-none"  aria-label="Scroll to Features Section">
                    {label}
                  </button>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Contact button for mobile */}
          <a 
            href="tel:+917200945514"
            className="boton-elegante mt-8 text-xl"
          >
            {t('header.callNow')}
          </a>
        </motion.div>
      )}
    </>
  );
};

export default Header;